// apps/cli/src/commands/enterprise/talents/list.ts
import type { Command } from 'commander';
import { asPaginatedBody } from '@wport/core';
import { enterpriseGet } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { dim, printJson, printTable, type OutputFormat } from '../../../lib/output';
import { parseFieldsList, pickPaths } from '../../../lib/path-utils';

interface ListFlags {
	tab?: string;
	job?: string;
	keyword?: string;
	from?: string;
	to?: string;
	page?: number;
	pageSize?: number;
	fields?: string;
	minimal?: boolean;
}

/** PRD §6.5：未帶 -s 時預設 page-size = 20（後端 PaginationDto 預設 10，故 CLI 顯式帶）。 */
const DEFAULT_PAGE_SIZE = 20;

const MINIMAL_LIST_FIELDS = ['enc_resume_id', 'candidate_name', 'applied_job_title', 'applied_at'];

/** 欄位對齊 server EnterpriseTalentListVm。PII-safe：無任何聯絡方式欄位；禁止 spread 未知欄位進表格。 */
interface EnterpriseTalentItem {
	enc_resume_id?: string;
	candidate_name?: string | null;
	highest_education?: string | null;
	latest_experience?: string | null;
	applied_job_title?: string | null;
	applied_at?: string | null;
	is_viewed?: boolean;
	[k: string]: unknown;
}

function formatDate(value: string | null | undefined): string {
	return value ? String(value).slice(0, 10) : '';
}

export async function runEnterpriseTalentsList(
	ctx: { baseUrl: string; locale: string; timeoutMs: number; format: OutputFormat; color: boolean },
	apiKey: string,
	flags: ListFlags
): Promise<void> {
	if (flags.fields && flags.minimal) {
		throw new CliError('Use either --fields or --minimal, not both', ExitCode.InvalidArgument);
	}
	const { body } = await enterpriseGet(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/talents',
		{
			tab: flags.tab,
			enc_job_id: flags.job,
			keyword: flags.keyword,
			start_date: flags.from,
			end_date: flags.to,
			currentPage: flags.page,
			pageSize: flags.pageSize ?? DEFAULT_PAGE_SIZE,
		}
	);
	const paged = asPaginatedBody<EnterpriseTalentItem>(body);

	const projection = flags.minimal ? MINIMAL_LIST_FIELDS : flags.fields ? parseFieldsList(flags.fields) : undefined;
	if (projection || ctx.format === 'json') {
		printJson(projection ? { ...paged, data: paged.data.map((row) => pickPaths(row, projection)) } : paged);
		return;
	}

	printTable(
		paged.data,
		[
			{ header: 'RESUME_ID', value: (r) => (r.enc_resume_id ?? '').slice(0, 14) },
			{ header: 'CANDIDATE', value: (r) => r.candidate_name ?? '', maxWidth: 20 },
			{ header: 'EDUCATION', value: (r) => r.highest_education ?? '', maxWidth: 18 },
			{ header: 'EXPERIENCE', value: (r) => r.latest_experience ?? '', maxWidth: 28 },
			{ header: 'APPLIED_JOB', value: (r) => r.applied_job_title ?? '', maxWidth: 24 },
			{ header: 'APPLIED_AT', value: (r) => formatDate(r.applied_at), maxWidth: 12 },
			{ header: 'VIEWED', value: (r) => (r.is_viewed ? 'yes' : 'no') },
		],
		ctx.color
	);
	const head = `Showing page ${paged.currentPage}/${paged.totalPages} (${paged.data.length} of ${paged.totalCount} applicants).`;
	const hint =
		paged.totalPages > paged.currentPage ? `  Next: wport enterprise talents list --page ${paged.currentPage + 1}` : '';
	process.stdout.write(dim(head + hint, ctx.color) + '\n');
}

export function registerEnterpriseTalentsList(parent: Command): void {
	parent
		.command('list')
		.description('List applicants in your talent pool')
		.option('--tab <tab>', 'applied | visit (default applied; visit is not yet supported by the server)')
		.option('--job <enc_job_id>', 'filter by job posting enc_id')
		.option('-k, --keyword <kw>', 'filter by candidate name keyword')
		.option('--from <date>', 'applied on/after this date (YYYY-MM-DD)')
		.option('--to <date>', 'applied on/before this date (YYYY-MM-DD)')
		.option('-p, --page <n>', 'page number (server: currentPage, default 1)', (v) => Number(v))
		.option('-s, --page-size <n>', `items per page (default ${DEFAULT_PAGE_SIZE}, max 100)`, (v) => Number(v))
		.option('--fields <list>', 'output selected fields as JSON (comma-separated dotted paths)')
		.option('--minimal', `output only ${MINIMAL_LIST_FIELDS.join(',')} as JSON`)
		.action(async (flags: ListFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runEnterpriseTalentsList(ctx, key, flags);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { DEFAULT_PAGE_SIZE, formatDate };
